import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import useAuth from "@/hooks/auth";
import { getStartupDetailSimple } from "@/services/api/Startup";
import { checkEmailDomainWithList as checkEmailDomain } from "@/utils/checkEmailDomain";
import {
  ArrowLeft,
  ArrowSquareOut,
  CircleNotch,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import toast from "react-hot-toast";
import GoogleSignInButton from "../auth/GoogleSignInButton";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import signupImage from "@/assets/images/signup-image.webp";

import AppContext from "@/AppContext";
import { loginPopup, registerPopup } from "@/Atoms";
import {
  sendTokenNotUser,
  verifyTokenForUserRegister,
} from "@/services/api/Master";
import { useAtom } from "jotai";
import ErrorMessage from "../ErrorMessage";
import InputTokenForAccountRegister from "./InputTokenForAccountRegister";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Drawer, DrawerContent } from "../ui/drawer";
import Image from "next/image";
import { X } from "@phosphor-icons/react/dist/ssr";

const SignUpUnicornTracker = ({ preventClose = false }) => {
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const [openRegisterPopup, setOpenRegisterPopup] = useAtom(registerPopup);
  const [isSendingVerifyEmail, setIsSendingVerifyEmail] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] =
    useState(false);
  const [emailConfirmLoading, setEmailConfirmLoading] = useState(false);
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [startup, setStartup] = useState(null);
  const [errors, setErrors] = useState([]);
  const [confirmToken, setConfirmToken] = useState("");
  const [sendEmailVerificationLoading, setSendEmailVerificationLoading] =
    useState(false);
  const [emailMatchesWebsite, setEmailMatchesWebsite] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const { user, login, isLoading, loginError } = useAuth({
    middleware: "guest",
    redirectIfAuthenticated: false,
  });

  const { query } = router;
  const type = query.purpose;
  const startup_id = query.startup_id;

  const context = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } = context.state;

  const isMobile = useIsMobile();

  const getStartup = async (id) => {
    const response = await getStartupDetailSimple(id);
    if (response.data) {
      setStartup(response.data);
    }
  };

  const emailInputRef = useRef();

  const onVerifyTokenUserRegister = async (e) => {
    e.preventDefault();
    if (!confirmToken) toast.error("Token is required!");
    setEmailVerificationLoading(true);
    setEmailConfirmLoading(true);
    try {
      const response = await verifyTokenForUserRegister({
        token: confirmToken,
        email,
      });
      try {
        await login({ email, password, setErrors, notRedirect: true });
        location.reload();
      } catch (error) {
        console.log(error);
      }
    } catch (error) {
      toast.error("Code is invalid or expired.");
    }
    setEmailVerificationLoading(false);
    setEmailConfirmLoading(false);
  };

  const onSendToken = async (e) => {
    if (!email) {
      return toast.error("Email is required!");
    }

    const isEmailMatching = await checkAndUpdateEmailDomain(
      email,
      startup?.website,
    );
    if (startup && !isEmailMatching) {
      return toast.error("The email must match the startup website!");
    }

    setEmailVerificationLoading(true);
    try {
      const response = await sendTokenNotUser({ email });
      toast.success(
        `Verification token just sent to ${email}, use that token to verify the email.`,
      );
      setIsSendingVerifyEmail(true);
    } catch (error) {}
    setEmailVerificationLoading(false);
  };

  const onResendToken = async (e) => {
    if (!email) {
      return toast.error("Email is required!");
    }

    const isEmailMatching = await checkAndUpdateEmailDomain(
      email,
      startup?.website,
    );
    if (startup && !isEmailMatching) {
      return toast.error("The email must match the startup website!");
    }

    setSendEmailVerificationLoading(true);
    try {
      const response = await sendTokenNotUser({ email });
      toast.success(
        `Verification token just sent to ${email}, use that token to verify the email.`,
      );
      setIsSendingVerifyEmail(true);
    } catch (error) {}
    setSendEmailVerificationLoading(false);
  };

  const checkAndUpdateEmailDomain = async (email, website) => {
    if (email && website) {
      const result = await checkEmailDomain(email, website);
      setEmailMatchesWebsite(result);
      return result;
    }
    return true;
  };

  useEffect(() => {
    checkAndUpdateEmailDomain(email, startup?.website);
  }, [email, startup?.website]);

  useEffect(() => {
    if (type === "register" || type === "claim") {
      if (startup_id) {
        getStartup(startup_id);
      } else {
        const startupName = query.startup_name;
        const startupWebsite = query.startup_website;
        if (startupName && startupWebsite) {
          setStartup({
            name: startupName,
            website: startupWebsite,
          });
        }
      }
    }
    emailInputRef.current?.focus();
  }, []);

  function switchPopup() {
    setOpenLoginPopup(false);
    setTimeout(() => {
      setOpenRegisterPopup(true);
    }, 50);
  }

  const submit = async function (evt) {
    evt.preventDefault();
    if (startup) {
      const isEmailMatching = await checkAndUpdateEmailDomain(
        email,
        startup.website,
      );
      if (!isEmailMatching) {
        return toast.error("The email must match the startup website!");
      }
    }
    await login({
      email,
      password,
      setErrors,
      startup,
      purpose: type,
    });
    refreshUserSubscription();
  };

  // Always prevent closing when user is not logged in
  const shouldPreventClose = preventClose || !user;

  // Determine the handler based on login status
  const handleOpenChange = useCallback(
    (open) => {
      if (shouldPreventClose) {
        if (open) setOpenLoginPopup(true);
      } else {
        setOpenLoginPopup(open);
      }
    },
    [shouldPreventClose, setOpenLoginPopup],
  );

  const handleInteractOutside = useCallback(
    (e) => {
      if (shouldPreventClose) {
        e.preventDefault();
      }
    },
    [shouldPreventClose],
  );

  const handleEscapeKeyDown = useCallback(
    (e) => {
      if (shouldPreventClose) {
        e.preventDefault();
      }
    },
    [shouldPreventClose],
  );

  // Don't show modal at all if user is already authenticated - check early to prevent flash
  if (user) {
    return null;
  }

  const isOpen = isSendingVerifyEmail ? true : openLoginPopup;

  const Content = (
    <div className='relative'>
      {!shouldPreventClose && (
        <button
          type='button'
          onClick={() => setOpenLoginPopup(false)}
          className='absolute z-10 inline-flex items-center justify-center text-black bg-white rounded-lg top-2 right-2 size-9'
        >
          <X weight='bold' className='text-xl' />
        </button>
      )}
      <div className='flex bg-white sm:gap-8 text-background'>
        <div className='sm:w-[340px]'>
          {isSendingVerifyEmail ? (
            <InputTokenForAccountRegister
              {...{
                isVisible: isOpen,
                onVerifyTokenUserRegister,
                emailVerificationLoading,
                confirmToken,
                setConfirmToken,
                isLoading,
                sendEmailVerificationLoading,
                onSendTokenForAccountRegistration: onResendToken,
              }}
            />
          ) : (
            <div className=''>
              <p className='text-2xl font-semibold leading-tight tracking-tight font-display'>
                Lucidity Regional Unicorn Tracker
              </p>
              <p className='mt-1 text-sm lg:mt-3 opacity-60'>
                Audited, searchable profiles of every unicorn across{" "}
                <span className='font-bold'>MEAPT</span> (Middle East, Africa,
                Pakistan & Turkey) with funding history, valuation ranges,
                investors, timelines, and much more. Curated by Lucidity
                Insights analysts; updated regularly.
              </p>
              <p className='mt-3 text-sm font-medium'>
                Create a free account to unlock full access.
              </p>
              <p className='mt-1 text-xs opacity-50'>
                Free access. No card required. Unsubscribe anytime.
              </p>
              <form onSubmit={submit} className='grid grid-cols-12 gap-4 mt-5'>
                {/* Email field */}
                <div className='col-span-12 space-y-1.5 text-left'>
                  <Label className='text-sm' htmlFor='email'>
                    Email <span className='text-red-500'>*</span>
                  </Label>
                  <Input
                    ref={emailInputRef}
                    type='email'
                    id='email'
                    required
                    onInput={(evt) => setEmail(evt.target.value)}
                    className='border-gray-300 shadow-sm bg-white/5 hover:bg-white/10 focus-visible:ring-0'
                  />
                  {loginError && (
                    <div className='flex items-center justify-between'>
                      <ErrorMessage message={loginError} />
                      {loginError === "Please verify your email first!" && (
                        <div
                          className='text-xs text-blue-600 cursor-pointer'
                          onClick={onSendToken}
                        >
                          {emailVerificationLoading ? (
                            <p className='animate-pulse text-slate-500'>
                              Sending verification code...
                            </p>
                          ) : (
                            "Verify Email"
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {startup && !emailMatchesWebsite && (
                    <small className='text-red-500'>
                      This email does not match the startup website!
                    </small>
                  )}
                </div>

                {/* Password field */}
                <div className='col-span-12 space-y-1.5 text-left'>
                  <Label className='text-sm' htmlFor='password'>
                    Password <span className='text-red-500'>*</span>
                  </Label>
                  <div className='relative'>
                    <Input
                      type={showPassword ? "text" : "password"}
                      id='password'
                      required
                      onInput={(evt) => setPassword(evt.target.value)}
                      className='border-gray-300 shadow-sm bg-white/5 hover:bg-white/10 focus-visible:ring-0'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute z-10 p-1 transition-colors -translate-y-1/2 right-3 top-1/2 text-black/60 hover:text-black'
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeSlash size={20} className='text-current' />
                      ) : (
                        <Eye size={20} className='text-current' />
                      )}
                    </button>
                  </div>
                </div>

                {/* Forgot password */}
                <div className='flex items-center justify-end col-span-12'>
                  <Link legacyBehavior href={"/forgot-password"} passHref>
                    <a className='inline-flex items-center text-sm font-medium underline duration-300 hover:opacity-70'>
                      Forgot Password
                    </a>
                  </Link>
                </div>

                {/* Submit + optional Google Sign-in */}
                <div className='flex flex-col items-center col-span-12 gap-4'>
                  <Button
                    type='submit'
                    variant='secondary'
                    className='w-full'
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <CircleNotch
                        weight='bold'
                        className='mr-2 text-lg animate-spin'
                      />
                    )}
                    {isLoading ? "Processing..." : "Login"}
                  </Button>
                  {!startup && (
                    <div className='flex flex-col items-center w-full gap-4'>
                      <div className='flex items-center w-full -my-1 space-x-3'>
                        <span className='flex-1 h-px bg-background/10'></span>
                        <span className='text-[10px] uppercase font-medium tracking-wider'>
                          or
                        </span>
                        <span className='flex-1 h-px bg-background/10'></span>
                      </div>
                      <GoogleSignInButton />
                    </div>
                  )}
                </div>
              </form>
              <div className='flex justify-center'>
                <button
                  type='button'
                  onClick={switchPopup}
                  className='mt-4 text-xs font-medium underline opacity-60'
                >
                  {`I don't have an account`}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className='relative flex-1 hidden overflow-hidden rounded-lg sm:block bg-background'>
          <Image
            src={signupImage.src}
            alt={""}
            layout='fill'
            sizes='(max-width: 768px)'
            className='object-cover scale-[110%]'
          />
        </div>
      </div>
      {/* Safe exits for visitors not ready to sign up. The popup atom is shared
          with the global login modal, so close it before leaving the page. */}
      {!isSendingVerifyEmail && (
        <div className='flex flex-col-reverse gap-3 pt-3 mt-5 border-t sm:flex-row sm:justify-end border-background/10'>
          <Link
            href='/'
            onClick={() => setOpenLoginPopup(false)}
            className='inline-flex items-center justify-center gap-2 px-4 text-sm font-medium transition-colors border border-gray-300 text-background rounded-md h-10 hover:bg-background/5'
          >
            <ArrowLeft weight='bold' />
            Back to Homepage
          </Link>
          <Link
            href='/unicorn-tracker/intro'
            onClick={() => setOpenLoginPopup(false)}
            className='inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-opacity rounded-md h-10 bg-gradient-to-l from-orange-1 to-red-1 hover:opacity-90'
          >
            <ArrowSquareOut weight='bold' />
            Learn More About Unicorn Tracker
          </Link>
        </div>
      )}
    </div>
  );

  return !isMobile ? (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className='p-3 bg-white !rounded-2xl max-w-[1000px]'
        hideCloseButton
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <DialogTitle className='sr-only'>
          Sign up for Unicorn Tracker
        </DialogTitle>
        <DialogDescription className='sr-only'>
          Login or register to access the Regional Unicorn Tracker
        </DialogDescription>
        {Content}
      </DialogContent>
    </Dialog>
  ) : (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className='bg-white text-background rounded-t-3xl max-h-[90dvh] overflow-hidden'>
        <div className='h-full p-5 pt-0 overflow-y-auto'>{Content}</div>
      </DrawerContent>
    </Drawer>
  );
};

export default SignUpUnicornTracker;
